import { useEffect, useState } from "react";
import { useMutation } from "convex/react";
import { useConvexAuth } from "convex/react";
import { api } from "convex/_generated/api";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  SparkleIcon,
  CheckIcon,
} from "@phosphor-icons/react";
import { Button, Input } from "@/components/ui/primitives";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { AIProviderStep, type AIProviderChoice } from "./steps/AIProviderStep";

const ACCENT_COLOR_PRESETS = [
  { hex: "#5a8a72", label: "Sage" },
  { hex: "#5b7ea8", label: "Steel" },
  { hex: "#8b7aad", label: "Lavender" },
  { hex: "#b86e6e", label: "Rose" },
  { hex: "#c08a5a", label: "Amber" },
  { hex: "#5a9ea8", label: "Teal" },
  { hex: "#6e6da0", label: "Indigo" },
] as const;

const ACCENT_VARS = [
  "--color-accent",
  "--color-accent-foreground",
  "--color-accent-soft",
  "--color-accent-medium",
] as const;

function getContrastColor(hex: string): string {
  const c = hex.replace("#", "");
  const r = Number.parseInt(c.substring(0, 2), 16);
  const g = Number.parseInt(c.substring(2, 4), 16);
  const b = Number.parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "#000000" : "#ffffff";
}

function useAccentColor(hex: string) {
  useEffect(() => {
    const root = document.documentElement;

    if (!hex) {
      for (const v of ACCENT_VARS) {
        root.style.removeProperty(v);
      }
      return;
    }

    root.style.setProperty("--color-accent", hex);
    root.style.setProperty("--color-accent-foreground", getContrastColor(hex));
    root.style.setProperty("--color-accent-soft", `${hex}1a`);
    root.style.setProperty("--color-accent-medium", `${hex}40`);

    return () => {
      for (const v of ACCENT_VARS) {
        root.style.removeProperty(v);
      }
    };
  }, [hex]);
}

// ── Option data ──

const INDUSTRIES = [
  "Technology / SaaS",
  "Financial Services",
  "Healthcare",
  "Manufacturing",
  "Retail / E-commerce",
  "Energy / Utilities",
  "Government",
  "Education",
  "Other",
];

const COMPANY_SIZES = ["1-10", "10-50", "50-250", "250-1000", "1000+"];

const COMPANY_SIZE_LABELS: Record<string, string> = {
  "1-10": "1 - 10",
  "10-50": "10 - 50",
  "50-250": "50 - 250",
  "250-1000": "250 - 1K",
  "1000+": "1000+",
};

const COMPANY_SIZE_DB_MAP: Record<string, string> = {
  "1-10": "1-10 employees",
  "10-50": "10-50 employees",
  "50-250": "50-250 employees",
  "250-1000": "250-1000 employees",
  "1000+": "1000+ employees",
};

const JURISDICTIONS = [
  "United States",
  "European Union",
  "United Kingdom",
  "Canada",
  "Australia",
  "Other",
];

// ── Animation variants ──

const ease = [0.25, 0.46, 0.45, 0.94] as const;

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 20 : -20,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.35, ease },
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -20 : 20,
    opacity: 0,
    transition: { duration: 0.2 },
  }),
};

const stagger = {
  hidden: { opacity: 0, y: 10 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.4,
      ease,
    },
  }),
};

// ── Selection chip component ──

function SelectionChip({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.97 }}
      className={`
        px-4 py-2.5 rounded-lg text-[13px] font-medium transition-all duration-200
        border cursor-pointer
        ${
          selected
            ? "border-accent bg-accent/8 text-accent"
            : "border-border bg-white text-secondary hover:border-border hover:bg-card-hover hover:text-primary"
        }
      `}
    >
      {label}
    </motion.button>
  );
}

// ── Panel content per step ──

const PANEL_CONTENT = [
  {
    headline: "Set up your workspace",
    subline:
      "Your workspace is where your team manages audits, documents, and compliance checklists.",
  },
  {
    headline: "Tell us more",
    subline:
      "This helps us tailor your experience and suggest relevant compliance frameworks.",
  },
  {
    headline: "AI-powered features",
    subline:
      "Choose how AI features like document analysis and checklist generation work in your workspace.",
  },
  {
    headline: "How do you want to start?",
    subline:
      "Start fresh or explore with pre-populated demo data — audits, checklists, and documents included.",
  },
];

// ── Main component ──

export function OnboardingWizard() {
  const navigate = useNavigate();
  const { isAuthenticated } = useConvexAuth();
  const createWorkspace = useMutation(api.workspaces.create);
  const saveAiProvider = useMutation(api.workspace_ai_provider.save);

  // Form state
  const [name, setName] = useState("");
  const [accentColor, setAccentColor] = useState("");
  const [industry, setIndustry] = useState("");
  const [companySize, setCompanySize] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [aiProviderChoice, setAiProviderChoice] =
    useState<AIProviderChoice | null>(null);
  const [withDemoData, setWithDemoData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Step state
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);

  useAccentColor(accentColor);

  const goToStep = (next: number) => {
    setDirection(next > step ? 1 : -1);
    setStep(next);
  };

  const canProceedFromDetails =
    name.trim().length > 0 &&
    industry.length > 0 &&
    companySize.length > 0 &&
    jurisdiction.length > 0;

  async function handleSubmit() {
    if (!canProceedFromDetails) return;

    setIsSubmitting(true);
    try {
      const { slug, workspaceId } = await createWorkspace({
        name: name.trim(),
        industry,
        companySize: COMPANY_SIZE_DB_MAP[companySize] ?? companySize,
        jurisdiction,
        withDemoData,
        accentColor: accentColor || undefined,
      });

      // Save AI provider config after workspace creation
      if (
        aiProviderChoice &&
        aiProviderChoice.provider === "byom_openai_compatible" &&
        aiProviderChoice.byomConfig
      ) {
        try {
          await saveAiProvider({
            workspaceId,
            provider: "byom_openai_compatible",
            byomRuntime: aiProviderChoice.byomConfig.runtime,
            byomEndpointUrl: aiProviderChoice.byomConfig.endpointUrl,
            byomModelName: aiProviderChoice.byomConfig.modelName,
            byomVisionModelName:
              aiProviderChoice.byomConfig.visionModelName || undefined,
            byomApiKey: aiProviderChoice.byomConfig.apiKey || undefined,
          });
        } catch {
          // Non-fatal — workspace was created, AI config can be set later in settings
          toast.error(
            "AI provider config could not be saved. You can configure it in Settings.",
          );
        }
      }
      // builtin_openai is the default — no save needed

      if (withDemoData) {
        toast.info("Setting up your demo data in the background...");
      }
      navigate({
        to: "/$workspaceSlug/inbox",
        params: { workspaceSlug: slug },
      });
    } catch (_err) {
      toast.error("Failed to create workspace. Please try again.");
      setIsSubmitting(false);
    }
  }

  const handleExitOnboarding = () => {
    navigate({ to: "/auth" });
  };

  const { headline, subline } = PANEL_CONTENT[step] ?? PANEL_CONTENT[0];

  return (
    <AuthLayout
      headline={headline}
      subline={subline}
      wide
      onBrandClick={isAuthenticated ? undefined : handleExitOnboarding}
    >
      {/* Progress bar */}
      <div className="mb-10 flex gap-2">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-1 flex-1 rounded-full overflow-hidden bg-border/50"
          >
            <motion.div
              className="h-full bg-accent rounded-full"
              initial={false}
              animate={{ width: step >= i ? "100%" : "0%" }}
              transition={{ duration: 0.4, ease }}
            />
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait" custom={direction}>
        {step === 0 ? (
          <motion.div
            key="step-0"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <StepName
              name={name}
              onNameChange={setName}
              accentColor={accentColor}
              onAccentColorChange={setAccentColor}
              onNext={() => goToStep(1)}
            />
          </motion.div>
        ) : step === 1 ? (
          <motion.div
            key="step-1"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <StepDetails
              industry={industry}
              companySize={companySize}
              jurisdiction={jurisdiction}
              onIndustryChange={setIndustry}
              onCompanySizeChange={setCompanySize}
              onJurisdictionChange={setJurisdiction}
              onBack={() => goToStep(0)}
              onNext={() => goToStep(2)}
              canProceed={canProceedFromDetails}
            />
          </motion.div>
        ) : step === 2 ? (
          <motion.div
            key="step-2"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <AIProviderStep
              onBack={() => goToStep(1)}
              onNext={(choice) => {
                setAiProviderChoice(choice);
                goToStep(3);
              }}
            />
          </motion.div>
        ) : (
          <motion.div
            key="step-3"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <StepStartMode
              withDemoData={withDemoData}
              onWithDemoDataChange={setWithDemoData}
              onBack={() => goToStep(2)}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </AuthLayout>
  );
}

// ── Step 1: Workspace name ──

function StepName({
  name,
  onNameChange,
  accentColor,
  onAccentColorChange,
  onNext,
}: {
  name: string;
  onNameChange: (v: string) => void;
  accentColor: string;
  onAccentColorChange: (v: string) => void;
  onNext: () => void;
}) {
  const canContinue = name.trim().length > 0;

  return (
    <div className="flex flex-col">
      <motion.div custom={0} variants={stagger} initial="hidden" animate="show">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-primary">
          What's your company called?
        </h1>
        <p className="mt-2 text-[14px] text-secondary leading-relaxed">
          This will be the name of your workspace in Auditera.
        </p>
      </motion.div>

      <div className="mt-8 flex flex-col gap-5">
        <motion.div
          custom={1}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-1.5"
        >
          <label
            htmlFor="workspace-name"
            className="text-[13px] font-medium text-secondary"
          >
            Workspace name
          </label>
          <Input
            id="workspace-name"
            type="text"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Acme Corp"
            autoFocus
          />
        </motion.div>

        <motion.div
          custom={2}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-1.5"
        >
          <span className="text-[13px] font-medium text-secondary">
            Accent color
          </span>
          <div className="flex items-center gap-2">
            {ACCENT_COLOR_PRESETS.map(({ hex, label }) => {
              const isSelected = accentColor === hex;
              return (
                <button
                  key={hex}
                  type="button"
                  title={label}
                  onClick={() => onAccentColorChange(isSelected ? "" : hex)}
                  className="relative w-8 h-8 rounded-full border-2 transition-all cursor-pointer"
                  style={{
                    backgroundColor: hex,
                    borderColor: isSelected ? hex : "transparent",
                    boxShadow: isSelected
                      ? `0 0 0 2px var(--color-page), 0 0 0 4px ${hex}`
                      : undefined,
                  }}
                >
                  {isSelected && (
                    <CheckIcon className="absolute inset-0 m-auto w-4 h-4 text-white" />
                  )}
                </button>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          custom={3}
          variants={stagger}
          initial="hidden"
          animate="show"
        >
          <Button
            type="button"
            variant="primary"
            disabled={!canContinue}
            onClick={onNext}
            className="w-full mt-1"
          >
            Continue
          </Button>
        </motion.div>
      </div>
    </div>
  );
}

// ── Step 2: Organization details with selection chips ──

function StepDetails({
  industry,
  companySize,
  jurisdiction,
  onIndustryChange,
  onCompanySizeChange,
  onJurisdictionChange,
  onBack,
  onNext,
  canProceed,
}: {
  industry: string;
  companySize: string;
  jurisdiction: string;
  onIndustryChange: (v: string) => void;
  onCompanySizeChange: (v: string) => void;
  onJurisdictionChange: (v: string) => void;
  onBack: () => void;
  onNext: () => void;
  canProceed: boolean;
}) {
  return (
    <div className="flex flex-col">
      {/* Back */}
      <motion.button
        type="button"
        onClick={onBack}
        custom={0}
        variants={stagger}
        initial="hidden"
        animate="show"
        className="flex items-center gap-1.5 text-[13px] text-secondary hover:text-primary transition-colors mb-6 self-start"
      >
        <ArrowLeftIcon className="size-3.5" />
        Back
      </motion.button>

      <motion.div custom={1} variants={stagger} initial="hidden" animate="show">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-primary">
          Tell us about your organization
        </h1>
        <p className="mt-2 text-[14px] text-secondary leading-relaxed">
          This helps us customize your compliance experience.
        </p>
      </motion.div>

      <div className="mt-8 flex flex-col gap-6">
        {/* Industry */}
        <motion.div
          custom={2}
          variants={stagger}
          initial="hidden"
          animate="show"
        >
          <p className="text-[13px] font-medium text-secondary mb-2.5">
            Industry
          </p>
          <div className="flex flex-wrap gap-2">
            {INDUSTRIES.map((opt) => (
              <SelectionChip
                key={opt}
                label={opt}
                selected={industry === opt}
                onClick={() => onIndustryChange(opt)}
              />
            ))}
          </div>
        </motion.div>

        {/* Company Size */}
        <motion.div
          custom={3}
          variants={stagger}
          initial="hidden"
          animate="show"
        >
          <p className="text-[13px] font-medium text-secondary mb-2.5">
            Company size
          </p>
          <div className="flex flex-wrap gap-2">
            {COMPANY_SIZES.map((opt) => (
              <SelectionChip
                key={opt}
                label={COMPANY_SIZE_LABELS[opt] ?? opt}
                selected={companySize === opt}
                onClick={() => onCompanySizeChange(opt)}
              />
            ))}
          </div>
        </motion.div>

        {/* Jurisdiction */}
        <motion.div
          custom={4}
          variants={stagger}
          initial="hidden"
          animate="show"
        >
          <p className="text-[13px] font-medium text-secondary mb-2.5">
            Primary jurisdiction
          </p>
          <div className="flex flex-wrap gap-2">
            {JURISDICTIONS.map((opt) => (
              <SelectionChip
                key={opt}
                label={opt}
                selected={jurisdiction === opt}
                onClick={() => onJurisdictionChange(opt)}
              />
            ))}
          </div>
        </motion.div>

        {/* Continue */}
        <motion.div
          custom={5}
          variants={stagger}
          initial="hidden"
          animate="show"
        >
          <Button
            type="button"
            variant="primary"
            disabled={!canProceed}
            onClick={onNext}
            className="w-full mt-1"
          >
            Continue
          </Button>
        </motion.div>
      </div>
    </div>
  );
}

// ── Step 3: How do you want to start? ──

function StepStartMode({
  withDemoData,
  onWithDemoDataChange,
  onBack,
  onSubmit,
  isSubmitting,
}: {
  withDemoData: boolean;
  onWithDemoDataChange: (v: boolean) => void;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}) {
  return (
    <div className="flex flex-col">
      {/* Back */}
      <motion.button
        type="button"
        onClick={onBack}
        custom={0}
        variants={stagger}
        initial="hidden"
        animate="show"
        className="flex items-center gap-1.5 text-[13px] text-secondary hover:text-primary transition-colors mb-6 self-start"
      >
        <ArrowLeftIcon className="size-3.5" />
        Back
      </motion.button>

      <motion.div custom={1} variants={stagger} initial="hidden" animate="show">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-primary">
          How do you want to start?
        </h1>
        <p className="mt-2 text-[14px] text-secondary leading-relaxed">
          You can always add your own data later.
        </p>
      </motion.div>

      <div className="mt-8 flex flex-col gap-3">
        {/* Start fresh option */}
        <motion.button
          type="button"
          custom={2}
          variants={stagger}
          initial="hidden"
          animate="show"
          onClick={() => onWithDemoDataChange(false)}
          className={`
            w-full text-left rounded-xl border p-4 transition-all duration-200 cursor-pointer
            ${
              !withDemoData
                ? "border-accent bg-accent/8 shadow-sm"
                : "border-border bg-white hover:bg-card-hover hover:border-border"
            }
          `}
        >
          <div className="flex items-start gap-3">
            <div
              className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg ${!withDemoData ? "bg-accent/15 text-accent" : "bg-muted text-secondary"}`}
            >
              <CheckCircleIcon className="size-4" />
            </div>
            <div>
              <p
                className={`text-[14px] font-semibold ${!withDemoData ? "text-accent" : "text-primary"}`}
              >
                Start fresh
              </p>
              <p className="mt-0.5 text-[13px] text-secondary leading-relaxed">
                Begin with an empty workspace. Add your own audits, documents,
                and checklists as you go.
              </p>
            </div>
          </div>
        </motion.button>

        {/* Demo data option */}
        <motion.button
          type="button"
          custom={3}
          variants={stagger}
          initial="hidden"
          animate="show"
          onClick={() => onWithDemoDataChange(true)}
          className={`
            w-full text-left rounded-xl border p-4 transition-all duration-200 cursor-pointer
            ${
              withDemoData
                ? "border-accent bg-accent/8 shadow-sm"
                : "border-border bg-white hover:bg-card-hover hover:border-border"
            }
          `}
        >
          <div className="flex items-start gap-3">
            <div
              className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg ${withDemoData ? "bg-accent/15 text-accent" : "bg-muted text-secondary"}`}
            >
              <SparkleIcon className="size-4" />
            </div>
            <div>
              <p
                className={`text-[14px] font-semibold ${withDemoData ? "text-accent" : "text-primary"}`}
              >
                Explore with demo data
              </p>
              <p className="mt-0.5 text-[13px] text-secondary leading-relaxed">
                Pre-populated with sample audits, checklist items, and document
                stubs so you can explore the app right away.
              </p>
            </div>
          </div>
        </motion.button>

        {/* Submit */}
        <motion.div
          custom={4}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="mt-2"
        >
          <Button
            type="button"
            variant="primary"
            loading={isSubmitting}
            onClick={onSubmit}
            className="w-full"
          >
            {isSubmitting ? "Creating workspace..." : "Create workspace"}
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
